import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { KpiCard, SectionTitle, StatusPill } from "@/components/dashboard/widgets";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import LiveLeadFeed from "@/components/dashboard/LiveLeadFeed";
import ReservedCampaignBanner from "@/components/dashboard/ReservedCampaignBanner";
import RoiWidget from "@/components/dashboard/RoiWidget";
import SourceQualityHeatmap from "@/components/dashboard/SourceQualityHeatmap";
import AddonsHint from "@/components/dashboard/AddonsHint";
import { Eye, ArrowUpRight } from "lucide-react";
import AIInsights from "@/components/dashboard/AIInsights";

function ReplayStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/client/replay-stats").then(r => setStats(r.data)).catch(() => setStats({ total_views: 0, top: [] })); }, []);
  if (!stats) return null;
  return (
    <Card className="rounded-xl shadow-soft border-slate-200" data-testid="replay-stats">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="inline-grid place-items-center size-8 rounded-md bg-primary/10 text-primary"><Eye className="size-4" /></span>
            <div className="leading-tight"><h3 className="font-medium text-slate-900">Replay views</h3><div className="text-[11px] text-slate-500">Public shareable enquiry replays</div></div>
          </div>
          <div className="text-right"><div className="text-2xl font-semibold tabular-nums text-slate-900">{stats.total_views}</div><div className="text-[10px] uppercase tracking-widest text-slate-500">total views</div></div>
        </div>
        {stats.top?.length > 0 ? (
          <ul className="mt-5 space-y-2">
            {stats.top.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-slate-800">"{s.intent || "Enquiry replay"}"</div>
                  <div className="text-[11px] text-slate-500 capitalize">{(s.sector || "—").toLowerCase()}</div>
                </div>
                <a href={`/replay/${s.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary font-mono">
                  {s.views} views <ArrowUpRight className="size-3" />
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-4 text-xs text-slate-500">Share your AI conversation replay link with prospects — every view counts.</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function Overview() {
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    api.get("/client/overview").then(r => setData(r.data));
    api.get("/client/leads?limit=8").then(r => setLeads(r.data));
  }, []);

  if (!data) return <div className="text-sm text-slate-500">Loading overview…</div>;
  const { kpis, series, by_sector } = data;

  return (
    <div className="space-y-8">
      <ReservedCampaignBanner />

      {/* AI Lead Coach — Claude-generated insights, cached 6h */}
      <AIInsights />

      <SectionTitle title="Campaign overview" subtitle="Live delivery, acceptance and economics — last 14 days." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Leads delivered" value={kpis.leads_delivered.toLocaleString()} delta="+12.4% vs prev 14d" testid="kpi-delivered" />
        <KpiCard label="Acceptance rate" value={`${kpis.acceptance_rate}%`} delta="+1.8%" testid="kpi-accept" />
        <KpiCard label="Avg CPL" value={kpis.avg_cpl.toFixed(2)} prefix="£" delta="−2.4%" deltaPositive testid="kpi-cpl" />
        <KpiCard label="Monthly spend" value={kpis.monthly_spend.toLocaleString()} prefix="£" delta="+8.1%" testid="kpi-spend" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 rounded-xl shadow-soft border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3"><h3 className="font-medium text-slate-900">Delivery & spend</h3><span className="text-xs text-slate-500">Last 14 days</span></div>
            <ResponsiveContainer width="100%" height={288} minWidth={0} minHeight={200}>
              <AreaChart data={series} margin={{ left: -8, right: 8, top: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="gd" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#0055FF" stopOpacity={0.28} /><stop offset="100%" stopColor="#0055FF" stopOpacity={0} /></linearGradient>
                  <linearGradient id="ga" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.24} /><stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="delivered" stroke="#0055FF" fill="url(#gd)" strokeWidth={2} />
                <Area type="monotone" dataKey="accepted" stroke="#0ea5e9" fill="url(#ga)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="rounded-xl shadow-soft border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3"><h3 className="font-medium text-slate-900">By sector</h3><span className="text-xs text-slate-500">Mix</span></div>
            <ResponsiveContainer width="100%" height={288} minWidth={0} minHeight={200}>
              <BarChart data={by_sector} layout="vertical" margin={{ left: 80, right: 8, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="sector" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={120} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Bar dataKey="leads" fill="#0055FF" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <ReplayStats />

      <AddonsHint />

      <RoiWidget />

      <SourceQualityHeatmap />

      <Card className="rounded-xl shadow-soft border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4"><h3 className="font-medium text-slate-900">Recent leads</h3><a href="/dashboard/leads" className="text-xs text-primary">View all →</a></div>
          <div className="grid lg:grid-cols-5 gap-5">
            <div className="lg:col-span-2"><LiveLeadFeed /></div>
            <div className="lg:col-span-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs uppercase tracking-widest text-slate-500 text-left">
                  <th className="py-2 font-medium">Name</th><th className="font-medium">Sector</th><th className="font-medium">City</th><th className="font-medium">Status</th><th className="font-medium text-right">Price</th>
                </tr></thead>
                <tbody>
                  {leads.slice(0, 8).map((l) => (
                    <tr key={l.id} className="border-t border-slate-100">
                      <td className="py-2.5 font-medium text-slate-900">{l.first_name} {l.last_name}</td>
                      <td className="text-slate-700 capitalize">{l.sector.replace("-", " ")}</td>
                      <td className="text-slate-600">{l.city}</td>
                      <td><StatusPill status={l.status} /></td>
                      <td className="text-right font-mono tabular-nums">£{l.price.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
